/** 语言上下文与切换器。与 `./index.ts` 分开是为了让文案表保持纯数据、可被脚本直接 import。 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { LOCALES, LOCALE_NAMES, type Locale, type Strings, detectLocale, storeLocale, t } from '.';

interface I18n {
  locale: Locale;
  s: Strings;
  setLocale: (next: Locale) => void;
}

const Ctx = createContext<I18n | null>(null);

export function LocaleProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  // `lang` 属性影响字体回退、断行、以及屏幕阅读器的发音。
  useEffect(() => {
    document.documentElement.lang = locale === 'zh' ? 'zh-CN' : 'en';
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    storeLocale(next);
    setLocaleState(next);
  }, []);

  const value = useMemo<I18n>(() => ({ locale, s: t(locale), setLocale }), [locale, setLocale]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useI18n(): I18n {
  const ctx = useContext(Ctx);
  // 抛错而不是回退到默认语言：忘记包 Provider 应当在开发期立刻炸掉，
  // 而不是安静地让一半界面用另一种语言。
  if (!ctx) throw new Error('useI18n 必须在 <LocaleProvider> 内使用');
  return ctx;
}

/** 语言切换器。登录前后都要能用 —— 看不懂界面的人正是最需要它的人。 */
export function LocaleSwitch({ compact = false }: { compact?: boolean }) {
  const { locale, setLocale } = useI18n();
  return (
    <div
      role="group"
      aria-label="Language"
      style={{ display: 'flex', gap: 6, justifyContent: compact ? 'flex-start' : 'center' }}
    >
      {LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          className={`btn btn-sm${locale === code ? ' btn-primary' : ''}`}
          aria-pressed={locale === code}
          onClick={() => setLocale(code)}
        >
          {LOCALE_NAMES[code]}
        </button>
      ))}
    </div>
  );
}
